import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Search, Activity, TrendingUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABEL = {
  "auth.login": "Login",
  "client.create": "Cadastrou cliente",
  "proposal.create": "Criou proposta",
  "proposal.status.aceita": "Aceitou proposta",
  "proposal.status.recusada": "Recusou proposta",
  "proposal.status.enviada": "Enviou proposta",
  "proposal.contract.generate": "Gerou contrato",
  "training.emit": "Emitiu certificados",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Cartão de métricas por ator — isolado para memoizar a ordenação das ações
 * e evitar recomputar em toda re-render do pai.
 */
function ActorMetricCard({ actor }) {
  const sortedActions = useMemo(
    () => Object.entries(actor.actions || {}).sort(([, x], [, y]) => y - x),
    [actor.actions]
  );
  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid={`metrics-actor-${actor.actor_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold">{actor.actor_name}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{actor.actor_role}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-mono font-bold text-primary">{actor.total}</div>
          <div className="text-[10px] text-muted-foreground">ações</div>
        </div>
      </div>
      <div className="mt-3 space-y-1">
        {sortedActions.map(([action, cnt]) => (
          <div key={action} className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{ACTION_LABEL[action] || action}</span>
            <span className="font-mono font-semibold">{cnt}</span>
          </div>
        ))}
      </div>
      {actor.last_at && (
        <div className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border">
          Última: {fmtDate(actor.last_at)}
        </div>
      )}
    </div>
  );
}

export default function ActivityLogsTab() {
  const [logs, setLogs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [days, setDays] = useState(30);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, m] = await Promise.all([
        api.get("/admin/activity-logs?limit=300"),
        api.get(`/admin/activity-logs/metrics?days=${days}`),
      ]);
      setLogs(l.data);
      setMetrics(m.data);
    } catch (e) {
      toast.error("Falha ao carregar logs");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return logs;
    return logs.filter((l) =>
      (l.actor_name || "").toLowerCase().includes(n) ||
      (l.action || "").toLowerCase().includes(n) ||
      JSON.stringify(l.details || {}).toLowerCase().includes(n),
    );
  }, [q, logs]);

  return (
    <div className="space-y-6" data-testid="tab-activity-logs-content">
      <p className="text-sm text-muted-foreground">
        Registro de todas as ações de admins e colaboradores no sistema — logins, criações, emissões e mudanças de status.
      </p>

      {/* Productivity summary */}
      <section className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h4 className="font-semibold text-sm">Produtividade — últimos </h4>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="h-8 px-2 rounded border border-input bg-background text-xs" data-testid="logs-metrics-days">
              <option value="7">7 dias</option>
              <option value="15">15 dias</option>
              <option value="30">30 dias</option>
              <option value="60">60 dias</option>
              <option value="90">90 dias</option>
            </select>
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="logs-refresh-btn">
            <RefreshCw className="w-3 h-3 mr-1" /> Atualizar
          </Button>
        </div>

        {metrics && (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="logs-metrics-grid">
            {metrics.actors.length === 0 && (
              <div className="col-span-3 text-center text-sm text-muted-foreground py-8 border border-dashed border-border rounded-lg">
                Nenhuma ação nos últimos {days} dias.
              </div>
            )}
            {metrics.actors.map((a) => (
              <ActorMetricCard key={a.actor_id} actor={a} />
            ))}
          </div>
        )}
      </section>

      {/* Raw log list */}
      <section className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" />
            <h4 className="font-semibold text-sm">Histórico de ações (últimas 300)</h4>
          </div>
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar por ator/ação..." className="pl-9 h-9" data-testid="logs-search-input" />
          </div>
        </div>
        <div className="border border-border rounded-lg overflow-auto max-h-[500px]" data-testid="logs-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 label-tech">Quando</th>
                <th className="text-left px-3 py-2 label-tech">Ator</th>
                <th className="text-left px-3 py-2 label-tech">Papel</th>
                <th className="text-left px-3 py-2 label-tech">Ação</th>
                <th className="text-left px-3 py-2 label-tech">Detalhes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`log-row-${l.id}`}>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  <td className="px-3 py-2">{l.actor_name || "—"}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">{l.actor_role}</Badge>
                  </td>
                  <td className="px-3 py-2">{ACTION_LABEL[l.action] || l.action}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground max-w-md">
                    {l.details && Object.keys(l.details).length > 0
                      ? Object.entries(l.details).map(([k, v]) => `${k}: ${v}`).join(" · ")
                      : "—"}
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="5" className="text-center py-8 text-muted-foreground">Nenhum log encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
